<template>
  <div
    :data-testid="sectionTag"
    class="numeric-stepper"
    :class="{ 'numeric-stepper--error': !!errorMessage }"
  >
    <div
      class="numeric-stepper__control"
      :class="{
        'numeric-stepper__control--disabled': disabled,
        'numeric-stepper__control--error': !!errorMessage
      }"
    >
      <button
        :data-testid="`${sectionTag}-decrement`"
        type="button"
        class="numeric-stepper__btn"
        :disabled="disabled || modelValue === null || modelValue <= min"
        :aria-label="$t('app.common.actions.decreaseValue')"
        @click="decrement"
        @pointerdown="startHold(decrement)"
        @pointerup="stopHold"
        @pointerleave="stopHold"
        @pointercancel="stopHold"
      >
        <v-icon :icon="mdiMinus" size="20" />
      </button>

      <input
        :data-testid="`${sectionTag}-value`"
        class="numeric-stepper__input body-regular-default"
        type="text"
        inputmode="numeric"
        :value="draft ?? (modelValue === null ? '' : String(modelValue))"
        :placeholder="blank ? blankLabel : undefined"
        :disabled="disabled"
        :aria-label="ariaLabel"
        @input="draft = ($event.target as HTMLInputElement).value"
        @blur="commit"
        @keydown.enter.prevent="commit"
        @keydown.escape="draft = null"
      >

      <button
        :data-testid="`${sectionTag}-increment`"
        type="button"
        class="numeric-stepper__btn"
        :disabled="disabled || (modelValue ?? min) >= max"
        :aria-label="$t('app.common.actions.increaseValue')"
        @click="increment"
        @pointerdown="startHold(increment)"
        @pointerup="stopHold"
        @pointerleave="stopHold"
        @pointercancel="stopHold"
      >
        <v-icon :icon="mdiPlus" size="20" />
      </button>
    </div>

    <div v-if="errorMessage" class="numeric-stepper__error">
      <v-icon :icon="mdiAlertOutline" size="12" />
      <span class="label-semibold-small">{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue'
import { mdiAlertOutline, mdiMinus, mdiPlus } from '@mdi/js'

import { $t } from '@/plugins/i18n'
import { tokens } from '@/plugins/vuetify'
import { clamp, stepDown, stepUp } from '@/utils/number'

// WNumericSelector's look, with two differences it lacks: + and − land on multiples of the step
// (held at min and max, so the maximum is always reachable), and the value can be typed exactly.
// `blank`: emptying the field saves null, a setting's "no limit", shown as `blankLabel`; + then starts from min.
const props = withDefaults(
  defineProps<{
    modelValue: number | null
    blank?: boolean
    blankLabel?: string
    min?: number
    max?: number
    step?: number
    disabled?: boolean
    errorMessage?: string
    ariaLabel?: string
    sectionTag: string
  }>(),
  { blank: false, blankLabel: '', min: 0, max: Infinity, step: 1, disabled: false, errorMessage: '', ariaLabel: undefined }
)

const emit = defineEmits<{ 'update:modelValue': [value: number | null] }>()

/** What the person is typing, until Enter or leaving the field commits it; null shows the saved value. */
const draft = ref<string | null>(null)

function setValue(value: number | null) {
  if (value !== props.modelValue) emit('update:modelValue', value)
}

function increment() {
  const current = props.modelValue ?? props.min
  if (current < props.max) setValue(stepUp(current, props.step, props.max))
}

function decrement() {
  if (props.modelValue !== null && props.modelValue > props.min) setValue(stepDown(props.modelValue, props.step, props.min))
}

function commit() {
  if (draft.value === null) return
  const text = draft.value.trim()
  draft.value = null
  if (text === '' && props.blank) return setValue(null)
  // Empty or not a number keeps the saved value; settings are whole numbers, so a decimal rounds.
  const typed = Number(text)
  if (text === '' || !Number.isFinite(typed)) return
  setValue(clamp(Math.round(typed), props.min, props.max))
}

const holdInitialDelayMs = 400
const holdStartIntervalMs = 150
const holdMinIntervalMs = 30
const holdAcceleration = 0.85
let holdTimer: ReturnType<typeof setTimeout> | null = null

function stopHold() {
  if (holdTimer) clearTimeout(holdTimer)
  holdTimer = null
}

function startHold(action: () => void) {
  stopHold()
  let delay = holdStartIntervalMs
  const tick = () => {
    action()
    delay = Math.max(holdMinIntervalMs, delay * holdAcceleration)
    holdTimer = setTimeout(tick, delay)
  }
  holdTimer = setTimeout(tick, holdInitialDelayMs)
}

onBeforeUnmount(stopHold)
</script>

<style scoped>
.numeric-stepper {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: v-bind('tokens.spacing.gap.icon');
  width: 140px;
}

.numeric-stepper__control {
  display: flex;
  align-items: center;
  height: 40px;
  border: 1px solid v-bind('tokens.color.border.default');
  border-radius: v-bind('tokens.borderRadius.control.medium');
  background-color: v-bind('tokens.color.background.surface.default');
  overflow: hidden;
}

.numeric-stepper__control--disabled {
  background-color: v-bind('tokens.color.background.surface.muted');
  border-color: v-bind('tokens.color.navigation.text.default');
}

.numeric-stepper__control--error {
  border-color: v-bind('tokens.color.input.border.error');
}

.numeric-stepper__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: none;
  background-color: v-bind('tokens.color.action.utility.surface');
  color: v-bind('tokens.color.action.utility.text');
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.numeric-stepper__btn:hover:not(:disabled) {
  background-color: v-bind('tokens.color.action.utility.hover');
}

.numeric-stepper__btn:active:not(:disabled) {
  background-color: v-bind('tokens.color.action.utility.active');
}

.numeric-stepper__btn:disabled {
  color: v-bind('tokens.color.action.utility.disabled.text');
  background-color: transparent;
  cursor: default;
}

.numeric-stepper__control--error .numeric-stepper__btn,
.numeric-stepper__control--error .numeric-stepper__input {
  color: v-bind('tokens.color.input.text.error');
}

.numeric-stepper__input {
  flex: 1 0 0;
  min-width: 0;
  height: 40px;
  border: none;
  outline: none;
  background-color: transparent;
  font-family: Inter, sans-serif;
  line-height: 20px;
  text-align: center;
  color: v-bind('tokens.color.action.utility.text');
}

.numeric-stepper__input:focus-visible {
  box-shadow: inset 0 0 0 2px v-bind('tokens.color.border.default');
}

.numeric-stepper__control--disabled .numeric-stepper__input {
  color: v-bind('tokens.color.action.utility.disabled.text');
}

.numeric-stepper__error {
  position: absolute;
  top: 100%;
  left: 0;
  display: flex;
  align-items: center;
  gap: v-bind('tokens.spacing.gap.icon');
  color: v-bind('tokens.color.input.text.error');
  white-space: nowrap;
}
</style>
